// Copyright 2020 The Nakama Authors
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Leaderboard, LeaderboardSearchFilters, LeaderboardSortOrder, LeaderboardOperator } from '../../types/leaderboards';
import { UserRole } from '../../types/auth';
import { consoleService } from '../../services/consoleService';
import { authService } from '../../services/authService';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import ConfirmDialog from '../ConfirmDialog';
import './Leaderboards.scss';

// SVG Icons
const RedTriangleIcon = () => (
  <img src="/static/svg/red-triangle.svg" alt="" width="16" height="16" className="mr-2" />
);

const PageFirstIcon = () => (
  <img src="/static/svg/page-first.svg" alt="First page" width="20" height="20" />
);

const PageNextIcon = () => (
  <img src="/static/svg/page-next.svg" alt="Next page" width="20" height="20" />
);

const LoadingSpinner = () => (
  <div className="spinner-border spinner-border-sm" role="status">
    <span className="visually-hidden">Loading...</span>
  </div>
);

const Leaderboards: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { dialogState, openConfirmDialog, closeConfirmDialog, handleConfirm } = useConfirmDialog();

  const [leaderboards, setLeaderboards] = useState<Leaderboard[]>([]);
  const [nextCursor, setNextCursor] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchFilters, setSearchFilters] = useState<LeaderboardSearchFilters>({
    leaderboard_id: '',
    category: undefined,
    authoritative: undefined,
  });

  const sessionRole = authService.getSessionRole();

  // Initialize filters from URL params
  useEffect(() => {
    const leaderboardId = searchParams.get('leaderboard_id') || '';
    const category = searchParams.get('category');
    const authoritative = searchParams.get('authoritative');
    const cursor = searchParams.get('cursor') || '';

    setSearchFilters({
      leaderboard_id: leaderboardId,
      category: category ? parseInt(category) : undefined,
      authoritative: authoritative ? authoritative === 'true' : undefined,
    });
    setNextCursor(cursor);

    if (cursor || leaderboardId || category || authoritative) {
      loadLeaderboards(cursor, {
        leaderboard_id: leaderboardId,
        category: category ? parseInt(category) : undefined,
        authoritative: authoritative ? authoritative === 'true' : undefined,
      });
    } else {
      loadLeaderboards();
    }
  }, []);

  const loadLeaderboards = useCallback(async (
    cursor?: string,
    filters?: LeaderboardSearchFilters
  ) => {
    setLoading(true);
    setError('');

    try {
      const result = await consoleService.listLeaderboards(
        '',
        filters || searchFilters,
        cursor
      );

      setLeaderboards(result.leaderboards || []);
      setNextCursor(result.cursor || '');
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load leaderboards');
    } finally {
      setLoading(false);
    }
  }, [searchFilters]);

  const handleSearch = useCallback((direction: number = 0) => {
    let cursor = '';
    switch (direction) {
      case 0:
        cursor = '';
        break;
      case 1:
        cursor = nextCursor;
        break;
    }

    // Update URL params
    const params = new URLSearchParams();
    if (searchFilters.leaderboard_id) params.set('leaderboard_id', searchFilters.leaderboard_id);
    if (searchFilters.category !== undefined) params.set('category', searchFilters.category.toString());
    if (searchFilters.authoritative !== undefined) params.set('authoritative', searchFilters.authoritative.toString());
    if (cursor) params.set('cursor', cursor);
    
    setSearchParams(params);
    loadLeaderboards(cursor, searchFilters);
  }, [searchFilters, nextCursor, setSearchParams, loadLeaderboards]);

  const handleFilterChange = (field: keyof LeaderboardSearchFilters, value: string | number | boolean | undefined) => {
    setSearchFilters(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSearch(0);
  };

  const handleDeleteLeaderboard = (leaderboard: Leaderboard, index: number) => {
    openConfirmDialog(
      'Delete Leaderboard',
      `Are you sure you want to delete the leaderboard "${leaderboard.id}"? This action cannot be undone and will remove all associated records.`,
      async () => {
        try {
          await consoleService.deleteLeaderboard('', leaderboard.id);
          setLeaderboards(prev => prev.filter((_, i) => i !== index));
          setError('');
        } catch (err: any) {
          setError(err.response?.data?.message || err.message || 'Failed to delete leaderboard');
        }
      }
    );
  };

  const handleViewLeaderboard = (leaderboard: Leaderboard) => {
    navigate(`/leaderboards/${leaderboard.id}`);
  };

  const deleteAllowed = () => {
    return sessionRole === UserRole.USER_ROLE_ADMIN;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString();
  };

  const getSortOrderText = (sortOrder: number) => {
    return sortOrder === LeaderboardSortOrder.ASCENDING ? 'Ascending' : 'Descending';
  };

  const getOperatorText = (operator: number) => {
    switch (operator) {
      case LeaderboardOperator.BEST: return 'Best';
      case LeaderboardOperator.SET: return 'Set';
      case LeaderboardOperator.INCREMENT: return 'Increment';
      case LeaderboardOperator.DECREMENT: return 'Decrement';
      default: return 'No Op';
    }
  };

  const formatMetadata = (metadata?: string) => {
    if (!metadata) return '';
    try {
      const parsed = JSON.parse(metadata);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return metadata;
    }
  };

  return (
    <div className="leaderboards-container">
      <div className="leaderboards-header">
        <h2 className="leaderboards-title">Leaderboards</h2>
        <h6 className="leaderboards-subtitle">{leaderboards.length} leaderboards found.</h6>
      </div>

      <div className="leaderboards-controls">
        <div className="search-section">
          <form onSubmit={handleSubmit} className="search-form">
            <div className="search-input-group">
              <input
                type="text"
                className="search-input"
                value={searchFilters.leaderboard_id || ''}
                onChange={(e) => handleFilterChange('leaderboard_id', e.target.value)}
                placeholder="Filter by leaderboard ID"
              />
              <select
                className="category-select"
                value={searchFilters.category || ''}
                onChange={(e) => handleFilterChange('category', e.target.value ? parseInt(e.target.value) : undefined)}
              >
                <option value="">All Categories</option>
                <option value="0">Category 0</option>
                <option value="1">Category 1</option>
                <option value="2">Category 2</option>
                <option value="3">Category 3</option>
                <option value="4">Category 4</option>
              </select>
              <select
                className="authoritative-select"
                value={searchFilters.authoritative === undefined ? '' : searchFilters.authoritative.toString()}
                onChange={(e) => handleFilterChange('authoritative', e.target.value === '' ? undefined : e.target.value === 'true')}
              >
                <option value="">All Types</option>
                <option value="true">Authoritative</option>
                <option value="false">Client</option>
              </select>
              <div className="search-buttons">
                <button
                  type="submit"
                  className="btn btn-primary search-btn"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <div className="spinner-border spinner-border-sm me-1" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      Searching...
                    </>
                  ) : (
                    'Search'
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>

        <div className="pagination-section">
          <div className="pagination-buttons">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => handleSearch(0)}
              disabled={leaderboards.length === 0 || loading}
              title="First page"
            >
              <PageFirstIcon />
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => handleSearch(1)}
              disabled={!nextCursor || loading}
              title="Next page"
            >
              <PageNextIcon />
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger">
          <RedTriangleIcon />
          <strong>Error when querying leaderboards:</strong> {error}
        </div>
      )}

      {loading && (
        <div className="alert alert-info">
          <LoadingSpinner />
          <strong className="ms-2">Loading leaderboards...</strong>
        </div>
      )}

      <div className="leaderboards-table-container">
        <table className="leaderboards-table">
          <thead>
            <tr>
              <th style={{ width: '200px' }}>Leaderboard ID</th>
              <th>Type</th>
              <th>Sort Order</th>
              <th>Operator</th>
              <th>Category</th>
              <th>Reset Schedule</th>
              <th style={{ width: '180px' }}>Created</th>
              {deleteAllowed() && <th style={{ width: '90px' }}>Remove</th>}
            </tr>
          </thead>
          <tbody>
            {leaderboards.length === 0 && !loading && (
              <tr>
                <td colSpan={deleteAllowed() ? 8 : 7} className="no-leaderboards">
                  No leaderboards found - change the filter criteria or create new leaderboards.
                </td>
              </tr>
            )}
            {leaderboards.map((leaderboard, index) => (
              <tr key={leaderboard.id} className="leaderboard-row">
                <td onClick={() => handleViewLeaderboard(leaderboard)} className="clickable">
                  <div className="leaderboard-id">{leaderboard.id}</div>
                  {leaderboard.title && (
                    <div className="leaderboard-title">{leaderboard.title}</div>
                  )}
                </td>
                <td onClick={() => handleViewLeaderboard(leaderboard)} className="clickable">
                  <span className={`type-badge ${leaderboard.authoritative ? 'authoritative' : 'client'}`}>
                    {leaderboard.authoritative ? 'Authoritative' : 'Client'}
                  </span>
                </td>
                <td onClick={() => handleViewLeaderboard(leaderboard)} className="clickable">
                  {getSortOrderText(leaderboard.sort_order)}
                </td>
                <td onClick={() => handleViewLeaderboard(leaderboard)} className="clickable">
                  {getOperatorText(leaderboard.operator)}
                </td>
                <td onClick={() => handleViewLeaderboard(leaderboard)} className="clickable">
                  {leaderboard.category !== undefined ? leaderboard.category : '-'}
                </td>
                <td onClick={() => handleViewLeaderboard(leaderboard)} className="clickable">
                  <code className="reset-schedule">
                    {leaderboard.reset_schedule || '-'}
                  </code>
                </td>
                <td onClick={() => handleViewLeaderboard(leaderboard)} className="clickable">
                  {formatDate(leaderboard.create_time)}
                </td>
                {deleteAllowed() && (
                  <td className="delete-cell">
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDeleteLeaderboard(leaderboard, index)}
                    >
                      Delete
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ConfirmDialog
        isOpen={dialogState.isOpen}
        title={dialogState.title}
        message={dialogState.message}
        onConfirm={handleConfirm}
        onCancel={closeConfirmDialog}
      />
    </div>
  );
};

export default Leaderboards;
